import axios, { AxiosInstance } from 'axios';
import { normaliserErreurApi } from '../lib/apiError';
import i18n from '../i18n';
import { API_BASE_URL, STORAGE_KEYS, PUBLIC_SITE_URL } from '../config/api';

export type OrganizerEmailMode = 'immediate' | 'daily' | 'none';

/**
 * Organisation de travail d'un ADMIN.
 *
 * Les écrans « organisation » (Paramètres, TVA, Stripe, Sync, jetons) agissaient
 * sur l'organisation du COMPTE connecté. Pour un admin qui assiste un client,
 * c'était la mauvaise : il travaillait sur l'événement d'une organisation et
 * voyait les réglages d'une autre. Le serveur résout l'organisation en un seul
 * endroit (`_appliquer_organisation_de_travail`) ; ici, un seul intercepteur
 * pose l'en-tête, plutôt que de passer un `organization_id` à chaque appel.
 *
 * ⚠️ Jamais posé pour un non-admin : le serveur refuse en 403, à dessein.
 */
export const WORKING_ORG_STORAGE_KEY = 'adminWorkingOrgId';
let workingOrganizationId: string | null = null;

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      // Envoyer les cookies HTTP-only automatiquement avec chaque requête
      withCredentials: true,
    });

    this.api.interceptors.request.use((config) => {
      if (workingOrganizationId) {
        config.headers = config.headers || {};
        config.headers['X-Organization-Id'] = workingOrganizationId;
      }
      return config;
    });

    // Intercepteur pour gérer les erreurs 401 avec refresh automatique
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Si erreur 401 et pas déjà en retry
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Tenter de rafraîchir le token via cookies
            await this.refreshAccessToken();

            // Retry la requête - les nouveaux cookies seront envoyés automatiquement
            return this.api.request(originalRequest);
          } catch (refreshError) {
            // Refresh échoué → déconnexion avec préservation de la destination
            console.error('❌ Refresh token échoué, déconnexion:', refreshError);
            this.clearAuth();
            window.location.href = `${PUBLIC_SITE_URL}/login?redirect=${encodeURIComponent(window.location.href)}`;
            return Promise.reject(refreshError);
          }
        }

        // Un `detail` de 422 est une liste d'objets : rendu tel quel par un
        // écran, il fait tomber la page. Ici il devient une chaîne, une fois
        // pour tous les lecteurs (`lib/apiError.ts`).
        return Promise.reject(normaliserErreurApi(error));
      }
    );
  }

  // Clear UI state (tokens are in HTTP-only cookies, managed by backend)
  clearAuth(): void {
    localStorage.removeItem(STORAGE_KEYS.USER);
  }

  private async refreshAccessToken(): Promise<void> {
    // Les cookies HTTP-only sont envoyés automatiquement
    const response = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      }
    );

    console.log('🔄 Tokens refreshed successfully (via cookies)');
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', {
      email,
      password
    });

    // Stocker uniquement les données utilisateur pour l'UI (pas les tokens)
    if (response.data.user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.user));
      console.log('✅ Login successful - Cookies set by server');
    }

    return response.data;
  }

  async logout() {
    try {
      // Appeler endpoint logout pour révoquer les tokens et clear les cookies
      await this.api.post('/auth/logout');
      console.log('✅ Logout successful - Cookies cleared by server');
    } catch (error) {
      console.error('⚠️ Erreur logout (non bloquant):', error);
    }

    this.clearAuth();
  }

  async validateToken() {
    return this.api.get('/auth/me');
  }

  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async resendVerificationEmail() {
    return this.api.post('/auth/resend-verification');
  }

  // Events endpoints
  async getEvents() {
    return this.api.get('/events');
  }

  async getEvent(id: string) {
    return this.api.get(`/events/${id}`);
  }

  async createEvent(event: any) {
    return this.api.post('/events', event);
  }

  async updateEvent(id: string, event: any) {
    return this.api.put(`/events/${id}`, event);
  }

  async notifyResults(eventId: string) {
    return this.api.post(`/events/${eventId}/notify-results`);
  }

  async deleteEvent(id: string) {
    return this.api.delete(`/events/${id}`);
  }

  async archiveEvent(id: string) {
    return this.api.put(`/events/${id}/archive`);
  }

  async restoreEvent(id: string) {
    return this.api.put(`/events/${id}/unarchive`);
  }

  async duplicateEvent(id: string) {
    return this.api.post(`/events/${id}/duplicate`);
  }

  // Page d'accueil d'un événement : chiffres clés, étapes de préparation et
  // ce qui reste à régler — calculé par le serveur, jamais dans la page.
  async getEventOverview(id: string) {
    return this.api.get(`/events/${id}/overview`);
  }

  // Teams endpoints
  async getTeams(eventId?: string) {
    const params = eventId ? `?event_id=${eventId}` : '';
    return this.api.get(`/teams${params}`);
  }

  async createTeam(team: any) {
    return this.api.post('/teams', team);
  }

  async updateTeam(id: string, team: any) {
    return this.api.put(`/teams/${id}`, team);
  }

  async deleteTeam(id: string) {
    return this.api.delete(`/teams/${id}`);
  }

  // Annulation d'inscription avec remboursement. Le motif est obligatoire :
  // rembourser debite la plateforme puis reprend l'argent chez l'organisateur,
  // et ce geste devra s'expliquer des mois plus tard.
  // `refundFees` : l'organisateur rend aussi les frais de service, a sa charge.
  async cancelRegistration(registrationId: string, reason: string, refundFees = false) {
    return this.api.post(`/registrations/${registrationId}/cancel`, {
      reason,
      refund_fees: refundFees,
    });
  }

  async importTeams(file: File, eventId: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('event_id', eventId);
    
    return this.api.post('/teams/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async downloadImportTemplate(): Promise<Blob> {
    const response = await this.api.get('/teams/import-template', { responseType: 'blob' });
    return response.data;
  }

  // Categories endpoints
  async getCategories(eventId?: string) {
    const params = eventId ? `?event_id=${eventId}` : '';
    return this.api.get(`/categories${params}`);
  }

  async createCategory(category: any) {
    return this.api.post('/categories', category);
  }

  async updateCategory(id: string, category: any) {
    return this.api.put(`/categories/${id}`, category);
  }

  async deleteCategory(id: string) {
    return this.api.delete(`/categories/${id}`);
  }

  async updateCategoriesOrder(eventId: string, categoryIds: string[]) {
    return this.api.put(`/events/${eventId}/categories/order`, { category_ids: categoryIds });
  }

  // WODs endpoints (CrossFit)
  async getWODs(eventId?: string) {
    const params = eventId ? `?event_id=${eventId}` : '';
    return this.api.get(`/wods${params}`);
  }

  async createWOD(wod: any) {
    return this.api.post('/wods', wod);
  }

  async updateWOD(id: string, wod: any) {
    return this.api.put(`/wods/${id}`, wod);
  }

  // ── Paliers de divulgation d'un WOD ────────────────────────────────
  // Deux gestes distincts, parce que l'organisateur en fait deux : basculer
  // maintenant, ou programmer. Les fondre obligerait l'écran à deviner lequel.

  /** Bascule IMMÉDIATE vers un palier. Reculer est permis — voir wodImpactRecul. */
  async setWodPalier(id: string, palier: 'draft' | 'mystery' | 'announced' | 'public') {
    return this.api.patch(`/wods/${id}/palier`, { palier });
  }

  /** Programme les bascules à venir. `null` sur un champ = « à la main ». */
  async setWodProgrammation(
    id: string, mystery_at: string | null, announced_at: string | null, public_at: string | null,
  ) {
    return this.api.put(`/wods/${id}/programmation`, { mystery_at, announced_at, public_at });
  }

  /** Ce qu'un recul ne pourra pas reprendre : heats annoncées, athlètes qui
   *  ont déjà reçu le nom. Lecture seule, appelée avant de confirmer. */
  async wodImpactRecul(id: string, vers: 'draft' | 'mystery' | 'announced' | 'public') {
    return this.api.get(`/wods/${id}/impact-recul`, { params: { vers } });
  }

  async deleteWOD(id: string) {
    return this.api.delete(`/wods/${id}`);
  }

  // Sync endpoints
  async getSyncStatus(eventCode: string) {
    return this.api.get(`/sync/events/${eventCode}/status`);
  }

  async syncResults(eventCode: string, results: any) {
    return this.api.post(`/sync/events/${eventCode}/results`, results);
  }

  // Settings endpoints
  async updateOrganization(orgData: any) {
    return this.api.put('/organizations/me', orgData);
  }

  /** Mandat de facturation : (ré)acceptation de la version courante des CGU. */
  async acceptOrganizationTerms() {
    const response = await this.api.post('/organizations/me/accept-terms');
    return response.data;
  }

  async getOrganizationFeatures() {
    const response = await this.api.get('/organizations/me/features');
    return response.data;
  }

  async getLocalApis() {
    const response = await this.api.get('/sync/local-apis');
    return response.data;
  }

  async getSyncHistory(filters?: { organizationId?: string; eventName?: string }) {
    const params = new URLSearchParams();
    if (filters?.organizationId) params.append('organization_id', filters.organizationId);
    if (filters?.eventName) params.append('event_name', filters.eventName);
    const queryString = params.toString();
    const response = await this.api.get(`/sync/history${queryString ? `?${queryString}` : ''}`);
    return response.data;
  }

  async getAllOrganizations() {
    const response = await this.api.get('/admin/organizations');
    return response.data;
  }

  async updateProfile(profileData: any) {
    return this.api.put('/users/me', profileData);
  }

  async deleteAllData() {
    return this.api.delete('/organizations/me/data');
  }

  // Registration Settings endpoints
  async getRegistrationSettings(eventId: string) {
    return this.api.get(`/events/${eventId}/registration-settings`);
  }

  async createRegistrationSettings(eventId: string, settings: any) {
    return this.api.post(`/events/${eventId}/registration-settings`, settings);
  }

  async updateRegistrationSettings(eventId: string, settings: any) {
    return this.api.put(`/events/${eventId}/registration-settings`, settings);
  }

  /** Mails de l'organisateur sur cet événement : chaque inscription / un récap par jour / aucun. */
  async getOrganizerEmailMode(eventId: string) {
    return this.api.get<{ organizer_email_mode: OrganizerEmailMode }>(`/events/${eventId}/organizer-email-mode`);
  }

  async setOrganizerEmailMode(eventId: string, mode: OrganizerEmailMode) {
    return this.api.put(`/events/${eventId}/organizer-email-mode`, { organizer_email_mode: mode });
  }

  async getEventRegistrations(eventId: string) {
    return this.api.get(`/events/${eventId}/registrations`);
  }

  // Heats endpoints
  async getTeamHeats(teamId: string) {
    return this.api.get(`/teams/${teamId}/heats`);
  }

  async getHeats() {
    return this.api.get('/heats');
  }

  async getEventHeats(eventId: string, wodId?: string) {
    const params: any = { event_id: eventId };
    if (wodId) {
      params.wod_id = wodId;
    }
    return this.api.get('/heats', { params });
  }

  async reorderHeats(eventId: string, heatOrders: { heat_id: string; new_position: number }[], recalculateTimes: boolean = true) {
    return this.api.put(`/events/${eventId}/heats/reorder`, {
      heat_orders: heatOrders,
      recalculate_times: recalculateTimes
    });
  }

  async moveTeamToHeat(sourceHeatId: string, teamId: string, targetHeatId: string, targetLaneNumber?: number) {
    return this.api.put(`/heats/${sourceHeatId}/participants/${teamId}/move`, {
      target_heat_id: targetHeatId,
      target_lane_number: targetLaneNumber
    });
  }

  async getHeatParticipants(heatId: string) {
    return this.api.get(`/heats/${heatId}/participants`);
  }

  /**
   * Participants de TOUTES les vagues d'un événement, en une requête.
   *
   * Le planning en faisait une par vague : 80 allers-retours sur Farm Race à
   * chaque ouverture, pour une information qui tient en une réponse. Même
   * forme que la route locale, servie triée par vague puis par couloir.
   */
  async getEventHeatParticipants(eventId: string) {
    return this.api.get(`/events/${eventId}/heat-participants`);
  }

  // --- Écriture du planning -------------------------------------------------
  //
  // Ces appels passaient par un `fetch` brut avec l'URL de prod en dur, donc
  // hors de l'intercepteur ci-dessus : le jeton d'accès expirant au bout de 15
  // minutes, une page de planning restée ouverte répondait « Erreur 401 » au
  // premier clic sur Générer, sans jamais tenter le rafraîchissement.

  async generateHeats(payload: Record<string, unknown>) {
    return this.api.post('/heats/generate', payload);
  }

  async createHeat(payload: Record<string, unknown>) {
    return this.api.post('/heats', payload);
  }

  async updateHeat(heatId: string, payload: Record<string, unknown>) {
    return this.api.put(`/heats/${heatId}`, payload);
  }

  async deleteHeat(heatId: string) {
    return this.api.delete(`/heats/${heatId}`);
  }

  /** Supprime une sélection ; les heats en cours ou terminées reviennent dans `skipped`. */
  async deleteHeats(eventId: string, heatIds: string[]) {
    const response = await this.api.post(`/events/${eventId}/heats/delete`, { heat_ids: heatIds });
    return response.data as {
      deleted_count: number;
      skipped: { heat_id: string; heat_number: number; status: string }[];
    };
  }

  /** Miroir de `importTeams` : même transport multipart, éprouvé en production. */
  async importHeats(file: File, eventId: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('event_id', eventId);

    return this.api.post('/heats/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async redistributeHeats(payload: Record<string, unknown>) {
    return this.api.post('/heats/redistribute', payload);
  }

  async publishHeatsPreview(eventId: string, heatIds?: string[]) {
    return this.api.post(`/events/${eventId}/heats/publish/preview`, { heat_ids: heatIds });
  }

  async notifyScheduleChange(eventId: string, heatIds: string[]) {
    return this.api.post(`/events/${eventId}/heats/notify-schedule-change`, { heat_ids: heatIds });
  }

  async moveHeatToDay(eventId: string, heatId: string, targetDate: string | null) {
    return this.api.post(`/events/${eventId}/heats/${heatId}/move-to-day`, { target_date: targetDate });
  }

  async publishHeats(eventId: string, heatIds?: string[]) {
    return this.api.post(`/events/${eventId}/heats/publish`, { heat_ids: heatIds });
  }

  /**
   * Statut de plusieurs heats d'un coup. N'envoie AUCUN email — annoncer aux
   * athlètes reste le travail de `publishHeats`.
   */
  async bulkUpdateHeatStatus(eventId: string, heatIds: string[], status: string) {
    const response = await this.api.put(`/events/${eventId}/heats/status`, {
      heat_ids: heatIds, status,
    });
    return response.data as {
      updated_count: number;
      status: string;
      skipped: { heat_id: string; heat_number: number; status: string }[];
    };
  }

  /**
   * Ouvrir ou fermer des créneaux d'inscription en lot.
   *
   * ASYMÉTRIQUE, et c'est le point : ouvrir publie la vague ET ouvre sa porte
   * (les deux conditions de `core/heat_slots.py`), fermer ne referme que la
   * porte — dépublier retirerait du planning public un horaire déjà annoncé.
   * N'envoie AUCUN email : un créneau s'adresse à des gens pas encore inscrits.
   */
  async bulkUpdateHeatSlots(eventId: string, openIds: string[], closeIds: string[]) {
    const response = await this.api.put(`/events/${eventId}/heats/slots`, {
      open_heat_ids: openIds, close_heat_ids: closeIds,
    });
    return response.data as {
      opened_count: number;
      closed_count: number;
      published_count: number;
      skipped: { heat_id: string; heat_number: number; status: string; reason: string }[];
      draft_wods: number[];
    };
  }

  // Méthodes génériques pour appels API custom
  async get(endpoint: string, params?: any) {
    return this.api.get(endpoint, { params });
  }

  async post(endpoint: string, data?: any) {
    // Si data est un FormData, on laisse axios gérer le Content-Type automatiquement
    // (il ajoutera multipart/form-data avec le boundary)
    if (data instanceof FormData) {
      return this.api.post(endpoint, data, {
        headers: {
          'Content-Type': undefined, // Supprime le header par défaut
        },
      });
    }
    return this.api.post(endpoint, data);
  }

  async put(endpoint: string, data?: any) {
    return this.api.put(endpoint, data);
  }

  async delete(endpoint: string, data?: any) {
    // `data` = corps de la requête (ex. le motif obligatoire d'un retrait de
    // résultat) ; axios l'attend dans la config, pas en second argument nu.
    return this.api.delete(endpoint, data === undefined ? undefined : { data });
  }

  /**
   * Télécharge un fichier servi par l'API et le propose à l'enregistrement.
   *
   * Passe par axios et non par un lien direct : un lien n'emporte pas
   * l'en-tête `X-Organization-Id`, donc un admin qui consulte une autre
   * organisation téléchargeait… depuis la sienne (« Organisation requise »),
   * et l'onglet quittait l'application pour l'URL de l'API.
   */
  async downloadFile(endpoint: string, fallbackName: string) {
    const response = await this.api.get(endpoint, { responseType: 'blob' });
    const disposition: string = response.headers['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/.exec(disposition);
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = match ? match[1] : fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // =============================================================================
  // NOTIFICATIONS
  // =============================================================================

  async getNotifications(limit: number = 50, offset: number = 0, unreadOnly: boolean = false) {
    const response = await this.api.get('/notifications', {
      params: { limit, offset, unread_only: unreadOnly }
    });
    return response.data;
  }

  async getNotificationsUnreadCount() {
    const response = await this.api.get('/notifications/unread-count');
    return response.data;
  }

  async markNotificationRead(notificationId: string) {
    const response = await this.api.put(`/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsRead() {
    const response = await this.api.put('/notifications/mark-all-read');
    return response.data;
  }

  async deleteNotification(notificationId: string) {
    const response = await this.api.delete(`/notifications/${notificationId}`);
    return response.data;
  }

  async getNotificationPreferences() {
    const response = await this.api.get('/notification-preferences');
    return response.data;
  }

  async updateNotificationPreferences(preferences: Record<string, boolean>) {
    const response = await this.api.put('/notification-preferences', preferences);
    return response.data;
  }

  // =============================================================================
  // EVENT INVITATIONS (événements privés)
  // =============================================================================

  async getEventInvitations(eventId: string) {
    const response = await this.api.get(`/events/${eventId}/invitations`);
    return response.data;
  }

  async addEventInvitations(eventId: string, emails: string[]) {
    const response = await this.api.post(`/events/${eventId}/invitations`, { emails });
    return response.data;
  }

  async removeEventInvitation(eventId: string, email: string) {
    const response = await this.api.delete(`/events/${eventId}/invitations/${encodeURIComponent(email)}`);
    return response.data;
  }

  // --- Staff d'événement : rôles juges (LOG-118) ---
  // --- Planning des juges (LOG-214) — CrossFit ---
  // ⚠️ C'est un DOCUMENT : rien dans le scoring ne lit ce plan.
  async getJudgePlan(eventId: string) {
    const response = await this.api.get(`/events/${eventId}/judge-plan`);
    return response.data;
  }

  async saveJudgePlan(
    eventId: string,
    assignments: Array<{ heat_id: string; lane_number: number; volunteer_id: string }>,
  ) {
    const response = await this.api.put(`/events/${eventId}/judge-plan`, { assignments });
    return response.data;
  }

  /** Le lien public du planning des juges (créé au premier appel, le même ensuite). */
  async shareJudgePlan(eventId: string) {
    const response = await this.api.post(`/events/${eventId}/judge-plan/share`);
    return response.data as { token: string; url: string };
  }

  /** Ce que le lien montre aux juges : les noms des WODs, ou seulement leur numéro. */
  async setJudgePlanShare(eventId: string, showWodNames: boolean) {
    const response = await this.api.put(`/events/${eventId}/judge-plan/share`, { show_wod_names: showWodNames });
    return response.data as { show_wod_names: boolean };
  }

  /** Une PROPOSITION de plan complété : le serveur n'écrit rien, l'écran l'affiche
   *  comme un plan non enregistré. */
  async autoAssignJudgePlan(eventId: string, body: {
    volunteer_ids: string[];
    assignments: Array<{ heat_id: string; lane_number: number; volunteer_id: string }>;
    day: string | null;
    rest_after: number;
    rest_heats: number;
  }) {
    const response = await this.api.post(`/events/${eventId}/judge-plan/auto-assign`, body);
    return response.data as {
      assignments: Array<{ heat_id: string; lane_number: number; volunteer_id: string }>;
      added: number;
      uncovered: Array<{ heat_id: string; lanes: number[] }>;
    };
  }

  async getEventStaff(eventId: string) {
    const response = await this.api.get(`/events/${eventId}/staff`);
    return response.data;
  }

  async addEventStaff(eventId: string, email: string, role: string, firstName?: string) {
    const response = await this.api.post(`/events/${eventId}/staff`, { email, role, first_name: firstName });
    return response.data;
  }

  async removeEventStaff(eventId: string, staffId: string) {
    const response = await this.api.delete(`/events/${eventId}/staff/${staffId}`);
    return response.data;
  }

  // --- Stations du parcours roxzone (LOG-123) ---
  async getCourseSegments(eventId: string) {
    const response = await this.api.get(`/events/${eventId}/course-segments`);
    return response.data;
  }

  async saveCourseSegments(eventId: string, segments: { station_name: string; laps: number }[]) {
    const response = await this.api.put(`/events/${eventId}/course-segments`, { segments });
    return response.data;
  }

  // Parcours d'une course par parties (LOG-225)
  async getCourseParts(wodId: string) {
    const response = await this.api.get(`/wods/${wodId}/course-parts`);
    return response.data;
  }

  async coursePartsFromStations(wodId: string) {
    const response = await this.api.post(`/wods/${wodId}/course-parts/from-stations`);
    return response.data;
  }

  async saveCourseParts(wodId: string, parts: unknown[]) {
    const response = await this.api.put(`/wods/${wodId}/course-parts`, { parts });
    return response.data;
  }

  // =============================================================================
  // TOKENS & LICENSES
  // =============================================================================

  // Installeurs de l'Organizer : réservés aux organisateurs (2026-10-07).
  async getOrganizerDownloads() {
    const response = await this.api.get('/downloads/organizer');
    return response.data;
  }

  async getOrganizerDownloadLink(platform: 'macos' | 'windows') {
    const response = await this.api.post(`/downloads/organizer/${platform}/link`);
    return response.data as { path: string; expires_at: string };
  }

  async getTokenBalance() {
    const response = await this.api.get('/tokens/balance');
    return response.data;
  }

  async getTokenPricing() {
    const response = await this.api.get('/tokens/pricing');
    return response.data;
  }

  async calculateTokenPrice(quantity: number, promoCode?: string, applyConnectDiscount?: boolean) {
    const response = await this.api.post('/tokens/calculate-price', {
      quantity,
      promo_code: promoCode || null,
      apply_connect_discount: applyConnectDiscount || false,
    });
    return response.data;
  }

  async purchaseTokens(quantity: number, promoCode?: string, applyConnectDiscount?: boolean) {
    const response = await this.api.post('/tokens/purchase', {
      quantity,
      promo_code: promoCode || null,
      apply_connect_discount: applyConnectDiscount || false,
    });
    return response.data;
  }

  /** Coût d'ouverture d'un événement (licence existante, ou jetons à consommer). */
  async validateEventLicense(eventId: string) {
    const response = await this.api.post('/licenses/validate', { event_id: eventId });
    return response.data;
  }

  async consumeTokens(eventId: string) {
    const response = await this.api.post('/tokens/consume', { event_id: eventId });
    return response.data;
  }

  async getTokenTransactions(limit?: number, offset?: number) {
    const response = await this.api.get('/tokens/transactions', { params: { limit, offset } });
    return response.data;
  }

  async getLicenseProducts() {
    const response = await this.api.get('/licenses/products');
    return response.data;
  }

  async getMyLicenses() {
    const response = await this.api.get('/licenses/my-licenses');
    return response.data;
  }

  async getEventLicenseStatus(eventId: string) {
    const response = await this.api.get(`/licenses/event/${eventId}`);
    return response.data;
  }

  async validatePromoCode(code: string, scope?: string) {
    const response = await this.api.post('/promo-codes/validate', { code, scope });
    return response.data;
  }

  // =============================================================================
  // ADMIN: LICENSES & TOKENS
  // =============================================================================

  async getAdminLicenses(params?: { organization_id?: string; status?: string; tier?: string }) {
    const response = await this.api.get('/admin/licenses', { params });
    return response.data;
  }

  async giftTokens(data: { organization_id: string; amount?: number; product_id?: string; event_id?: string; reason?: string }) {
    const response = await this.api.post('/admin/tokens/gift', data);
    return response.data;
  }

  async getAdminTransactions(params?: { organization_id?: string; transaction_type?: string; limit?: number; offset?: number }) {
    const response = await this.api.get('/admin/transactions', { params });
    return response.data;
  }

  async getAdminTransactionsSummary() {
    const response = await this.api.get('/admin/transactions/summary');
    return response.data;
  }

  // Revenus LogFit (admin) : par événement, jetons, total, état du rapprochement Stripe.
  async getAdminRevenue() {
    const response = await this.api.get('/admin/revenue');
    return response.data;
  }

  // Encaissé via LogFit par MON organisation, par événement (badge app bar + page Encaissements).
  async getMyRevenue() {
    const response = await this.api.get('/organizations/me/revenue');
    return response.data;
  }

  async getAdminPromoCodes() {
    const response = await this.api.get('/admin/promo-codes');
    return response.data;
  }

  async createPromoCode(data: any) {
    const response = await this.api.post('/admin/promo-codes', data);
    return response.data;
  }

  async updatePromoCode(id: string, data: any) {
    const response = await this.api.put(`/admin/promo-codes/${id}`, data);
    return response.data;
  }

  async deletePromoCode(id: string) {
    const response = await this.api.delete(`/admin/promo-codes/${id}`);
    return response.data;
  }

  // =============================================================================
  // EVENT MESSAGES (messagerie organisateur)
  // =============================================================================

  async getEventMessages(eventId: string) {
    const response = await this.api.get(`/events/${eventId}/messages`);
    return response.data;
  }

  async sendEventMessage(
    eventId: string,
    data: { subject: string; html_content: string; recipient_filter: any; attachment_ids?: string[] }
  ) {
    const response = await this.api.post(`/events/${eventId}/messages/send`, data);
    return response.data;
  }

  /**
   * Téléverse une image (corps du message) ou une pièce jointe. Le fichier est
   * gardé côté serveur avant l'envoi : les emails partent après la réponse.
   */
  async uploadEventMessageFile(eventId: string, file: File, kind: 'image' | 'attachment') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('kind', kind);
    const response = await this.api.post(`/events/${eventId}/messages/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as {
      id: string;
      kind: 'image' | 'attachment';
      name: string;
      size_bytes: number;
      content_type: string;
      url?: string;
      width?: number;
    };
  }

  /** Retire une pièce jointe pas encore envoyée. */
  async deleteEventMessageFile(eventId: string, fileId: string) {
    await this.api.delete(`/events/${eventId}/messages/files/${fileId}`);
  }

  /** Relit une pièce jointe envoyée (historique). */
  async downloadEventMessageFile(eventId: string, fileId: string) {
    const response = await this.api.get(`/events/${eventId}/messages/files/${fileId}`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  /** Nombre exact de destinataires d'un périmètre, sans envoyer. */
  async previewEventMessageRecipients(eventId: string, recipientFilter: any, signal?: AbortSignal) {
    const response = await this.api.post(
      `/events/${eventId}/messages/preview`,
      recipientFilter,
      { signal }
    );
    return response.data as { recipient_count: number };
  }

  // === Event Promo Codes ===

  async getEventPromoCodes(eventId: string) {
    const response = await this.api.get(`/events/${eventId}/promo-codes`);
    return response.data;
  }

  async createEventPromoCode(eventId: string, data: any) {
    const response = await this.api.post(`/events/${eventId}/promo-codes`, data);
    return response.data;
  }

  async updateEventPromoCode(eventId: string, codeId: string, data: any) {
    const response = await this.api.put(`/events/${eventId}/promo-codes/${codeId}`, data);
    return response.data;
  }

  async deleteEventPromoCode(eventId: string, codeId: string) {
    const response = await this.api.delete(`/events/${eventId}/promo-codes/${codeId}`);
    return response.data;
  }

  async getOrganizations() {
    const response = await this.api.get('/admin/organizations');
    return response.data;
  }

  // Category Change (differential payment)
  async getCategoryChangePreview(teamId: string, newCategoryId: string) {
    const response = await this.api.get('/stripe/category-change/preview', {
      params: { team_id: teamId, new_category_id: newCategoryId }
    });
    return response.data;
  }

  async requestCategoryChange(data: { team_id: string; new_category_id: string; event_id: string }) {
    const response = await this.api.post('/stripe/category-change', data);
    return response.data;
  }

  // --- AI ---

  /// Conversation WODEE (même endpoint que l'app Athlète), en audience
  /// organisateur : prescription seulement, un WOD = une entrée. La langue de
  /// l'interface est envoyée : déduite d'un WOD de trois mots (« AMRAP 10 min :
  /// Burpees »), elle retombait sur l'anglais.
  async chatWod(payload: { session_id?: string; message?: string; image?: string; image_type?: string; action: 'message' | 'validate' }) {
    const langue = (i18n.resolvedLanguage || i18n.language || 'fr').slice(0, 2);
    const response = await this.api.post('/ai/chat-wod', {
      ...payload,
      audience: 'organizer',
      language: ['fr', 'en', 'nl', 'es'].includes(langue) ? langue : 'fr',
    });
    return response.data;
  }

  async generateWodFromText(prompt: string) {
    // Un organisateur écrit le WOD de tous les athlètes : prescription
    // seulement, jamais d'`execution` (docs/features/athlete-workout-model.md).
    const response = await this.api.post('/ai/generate-wod', { prompt, audience: 'organizer' });
    return response.data;
  }

  async parseScore(text: string, scoringType: string = 'time') {
    const response = await this.api.post('/ai/parse-score', { text, scoring_type: scoringType });
    return response.data;
  }

  async getAiStatus() {
    const response = await this.api.get('/ai/status');
    return response.data;
  }

  async unloadAiModel() {
    const response = await this.api.delete('/ai/model');
    return response.data;
  }

  /**
   * Déclare l'organisation sur laquelle l'admin travaille. `null` = la sienne
   * (ou aucune). Appelé par OrgContext, seul propriétaire de cette décision.
   */
  setWorkingOrganizationId(organizationId: string | null) {
    workingOrganizationId = organizationId;
  }
}

const apiService = new ApiService();
export { apiService };
export default apiService;